import React from 'react';

import { useIntl } from 'react-intl';

import GatewayPricingPlanFormModal from 'src/features/gateway/components/form/GatewayPricingPlanFormModal';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { LIST_GATEWAY_PRICING_PLANS_RESOURCE } from 'src/features/gateway/api/useListGatewayPricingPlans';
import { useUpdateGatewayPricingPlan } from 'src/features/gateway/api/useUpdateGatewayPricingPland';
import { GatewayPricingPlan } from 'src/features/gateway/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface GatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedGatewayPricingPlan: GatewayPricingPlan;
    children?: React.ReactNode;
}

function UpdateGatewayFormModal<TData>({
    className,
    semantics,
    children,
    selectedGatewayPricingPlan,
}: GatewayFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateGatewayPricingPlan({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAY_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(gatewayMessages.updateGatewaySuccess),
                description: formatMessage(gatewayMessages.updateGatewaySuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <GatewayPricingPlanFormModal
            selectedGatewayPricingPlan={selectedGatewayPricingPlan}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, planId: selectedGatewayPricingPlan.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.editPricingPlanSubtitle, {
                entry: '',
            })}
        >
            {children}
        </GatewayPricingPlanFormModal>
    );
}

export default UpdateGatewayFormModal;
